import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { SITE_NAME, SITE_URL, absoluteUrl } from "@/lib/seo/schema";

const DEFAULT_OG_IMAGE = "/og-image.webp";

const LOCALE_BY_LANG: Record<string, string> = {
  sk: "sk_SK",
};

// Tags baked into the static index.html (for non-JS clients like social-share
// unfurlers, which never run this code). Once React hydrates, this component
// owns these tags via Helmet, so the static originals - which Helmet has no
// way of knowing about - are removed to avoid duplicate/stale meta in the DOM.
const STATIC_TAG_SELECTORS = [
  'meta[name="description"]',
  'link[rel="canonical"]',
  'meta[name="robots"]',
  'meta[property="og:url"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:image"]',
  'meta[property="og:image:width"]',
  'meta[property="og:image:height"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
  'meta[name="twitter:image"]',
];

function stripStaticHeadFallbacks() {
  STATIC_TAG_SELECTORS.forEach((selector) => {
    document.querySelectorAll(`${selector}:not([data-rh])`).forEach((el) => el.remove());
  });
  document.getElementById("ld-organization-static")?.remove();
}

interface SeoProps {
  title: string;
  description: string;
  path: string;
  lang?: "sk";
  noindex?: boolean;
  ogImage?: string;
  jsonLd?: object | object[];
  alternates?: { hrefLang: string; path: string }[];
}

const Seo = ({
  title,
  description,
  path,
  lang = "sk",
  noindex = false,
  ogImage = DEFAULT_OG_IMAGE,
  jsonLd,
  alternates,
}: SeoProps) => {
  const url = absoluteUrl(path);
  const image = absoluteUrl(ogImage);
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  useEffect(() => {
    stripStaticHeadFallbacks();
  }, []);

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow"} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={LOCALE_BY_LANG[lang] ?? "sk_SK"} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {alternates?.map((alt) => (
        <link key={alt.hrefLang} rel="alternate" hrefLang={alt.hrefLang} href={absoluteUrl(alt.path)} />
      ))}

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
export { SITE_URL, STATIC_TAG_SELECTORS };
